// src/components/FileUploadComponent.tsx
// File Upload Component - Now focused only on uploading new files and showing their progress.

import React, { useState, useRef, useEffect } from 'react';
import { Upload, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { FileStorageService } from '../services/fileStorageService';
import { AttachmentManager, type Attachment, type FileUploadProgress, type AttachmentType } from '../types/attachments';
import { pdfToImages } from '../utils/pdfToImages';
import { compressImage, compressDataUrl } from '../utils/compressImage';

interface FileUploadComponentProps {
  projectId: number;
  itemId: number; // Could be a timestamp for new items before they have a final ID
  userId: string;
  attachments: Attachment[]; // Current attachments list from parent (for maxFiles check)
  onAttachmentsChange: (attachments: Attachment[]) => void; // To update parent with newly uploaded files
  disabled?: boolean;
  maxFiles?: number;
  onUploadingStateChange?: (isUploading: boolean) => void;
  t: (key: string, options?: any) => string;
  compact?: boolean; // New prop for compact button mode
  attachmentType?: AttachmentType; // Type of attachment (proof_of_purchase, product_picture, or general)
}

export const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
  projectId,
  itemId,
  userId,
  attachments, // Used to check current count against maxFiles
  onAttachmentsChange,
  disabled = false,
  maxFiles = 10,
  onUploadingStateChange,
  t,
  compact = false, // Default to full mode
  attachmentType = 'general' // Default to general attachment type
}) => {
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onUploadingStateChange?.(uploading);
  }, [uploading, onUploadingStateChange]);

  const handleFileSelect = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0 || disabled) return;

    const filesToUploadArray = Array.from(selectedFiles);

    if (attachments.length + filesToUploadArray.length > maxFiles) {
      alert(t('file.upload.maxFiles.alert', {
        maxFiles,
        count: maxFiles - attachments.length,
        defaultValue: `Maximum ${maxFiles} files allowed. You can upload ${maxFiles - attachments.length} more.`
      }));
      return;
    }

    const validatedFiles: File[] = [];
    const validationErrors: string[] = [];
    for (const file of filesToUploadArray) {
      const validation = AttachmentManager.validateFile(file); // Assuming validateFile exists
      if (validation.valid) {
        validatedFiles.push(file);
      } else {
        validationErrors.push(validation.error || `${file.name}: Invalid file`);
      }
    }

    if (validationErrors.length > 0) {
      alert(t('file.upload.validation.errors', { errors: validationErrors.join('\n'), defaultValue: `File validation errors:\n${validationErrors.join('\n')}` }));
    }

    if (validatedFiles.length > 0) {
      await uploadFiles(validatedFiles);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    const currentUploadProgressItems: FileUploadProgress[] = files.map(file => ({
      file, progress: 0, status: 'uploading', stage: 'pending', id: `up-${file.name}-${Date.now()}`
    }));
    setUploadProgress(prev => [...prev, ...currentUploadProgressItems]);

    const newlyUploadedAttachments: Attachment[] = [];

    for (const progressItem of currentUploadProgressItems) {
      const { file } = progressItem;
      // Update stage to validating immediately
      setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, stage: 'validating' } : p));

      try {
        // Handle PDF conversion
        if (file.type === 'application/pdf') {
          try {
            // Update stage to converting
            setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, stage: 'converting' } : p));

            const pdfUrl = URL.createObjectURL(file);
            const pdfImages = await pdfToImages(pdfUrl, 1.5, (page, total) =>
              setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, totalPages: total, convertedPages: page } : p))
            );
            URL.revokeObjectURL(pdfUrl);

            if (!pdfImages || pdfImages.length === 0) {
              throw new Error('No images generated from PDF');
            }


            // Update stage to uploading
            setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, stage: 'uploading', progress: 1 } : p));

            // Upload each image (compressed)
            for (let i = 0; i < pdfImages.length; i++) {
              const imgSrc = pdfImages[i];
              const imgBlob = await compressDataUrl(imgSrc);
              const imgFile = new File([imgBlob], `${file.name}_page_${i + 1}.jpg`, { type: 'image/jpeg' });


              const attachmentId = `att-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
              const storedAttachmentData = await FileStorageService.uploadFile(
                imgFile, projectId, itemId, userId, attachmentId
              );


              const newAttachment: Attachment = {
                id: storedAttachmentData.id,
                name: `${file.name} - Page ${i + 1}`,
                type: 'image/jpeg',
                size: storedAttachmentData.size,
                url: storedAttachmentData.url,
                storagePath: storedAttachmentData.storagePath,
                uploadedAt: storedAttachmentData.uploadedAt,
                uploadedBy: storedAttachmentData.uploadedBy,
                isLegacy: false,
                attachmentType: attachmentType
              };

              newlyUploadedAttachments.push(newAttachment);
            }


            // After all images uploaded for this PDF
            setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, progress: 100, status: 'completed', stage: 'completed' } : p));

          } catch (pdfError: any) {
            // Update status to error and stage to error
            setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, status: 'error', stage: 'error', error: pdfError?.message || 'PDF conversion failed' } : p));

            // Fallback: If PDF conversion fails, upload the original PDF
            try {
              // Update stage to uploading for fallback
              setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, stage: 'uploading', progress: 1 } : p));

              const attachmentId = `att-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
              const storedAttachmentData = await FileStorageService.uploadFile(
                file, projectId, itemId, userId, attachmentId,
                (snapshot) => {
                  const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, progress: Math.round(progress) } : p));
                }
              );

              const newAttachment: Attachment = {
                id: storedAttachmentData.id,
                name: file.name,
                type: file.type,
                size: storedAttachmentData.size,
                url: storedAttachmentData.url,
                storagePath: storedAttachmentData.storagePath,
                uploadedAt: storedAttachmentData.uploadedAt,
                uploadedBy: storedAttachmentData.uploadedBy,
                isLegacy: false,
                attachmentType: attachmentType
              };
              newlyUploadedAttachments.push(newAttachment);
              // After successful fallback upload
              setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, progress: 100, status: 'completed', stage: 'completed' } : p));

            } catch (fallbackError: any) {
              // Update status to error and stage to error for fallback failure
              setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, status: 'error', stage: 'error', error: fallbackError?.message || 'Fallback upload failed' } : p));
            }
          }
        } else {
          // Handle non-PDF files - compress images before upload
          // Compress image files before upload
          const fileToUpload = file.type.startsWith('image/') ? await compressImage(file) : file;
          // Update stage to uploading
          setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, stage: 'uploading' } : p));

          const attachmentId = `att-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
          const storedAttachmentData = await FileStorageService.uploadFile(
            fileToUpload, projectId, itemId, userId, attachmentId,
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, progress: Math.round(progress) } : p));
            }
          );

          const newAttachment: Attachment = {
            id: storedAttachmentData.id,
            name: storedAttachmentData.name,
            type: storedAttachmentData.type,
            size: storedAttachmentData.size,
            url: storedAttachmentData.url,
            storagePath: storedAttachmentData.storagePath,
            uploadedAt: storedAttachmentData.uploadedAt,
            uploadedBy: storedAttachmentData.uploadedBy,
            isLegacy: false,
            attachmentType: attachmentType
          };
          newlyUploadedAttachments.push(newAttachment);
          // After successful upload
          setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, progress: 100, status: 'completed', stage: 'completed' } : p));
        }

      } catch (error: any) {
        setUploadProgress(prev => prev.map(p => p.id === progressItem.id ? { ...p, status: 'error', stage: 'error', error: error?.message || 'Upload failed' } : p));
      }
    }


    // Update parent with newly uploaded attachments
    onAttachmentsChange([...attachments, ...newlyUploadedAttachments]);
    setUploading(false);

    // Clean up completed uploads after a delay
    setTimeout(() => {
      setUploadProgress(prev => prev.filter(p => p.stage !== 'completed'));
    }, 2000);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  return (
    <div className={compact ? "" : "space-y-4"}>
      {/* Upload Area: Compact Button or Full Dropzone */}
      {!disabled && (
        compact ? (
          // Compact button mode
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors border border-blue-200"
          >
            <Upload className="w-3.5 h-3.5" />
            {t('action.uploadFile', { defaultValue: 'Upload' })}
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e.target.files)}
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
              className="hidden"
              disabled={disabled}
            />
          </button>
        ) : (
          // Full dropzone mode
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${dragOver
                ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-slate-700'
                : 'border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500'
              }`}
            onClick={() => !disabled && fileInputRef.current?.click()} // Make the whole area clickable
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label={t('file.upload.dropzone.aria', { defaultValue: 'File upload dropzone' })}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e.target.files)}
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
              className="hidden"
              disabled={disabled}
            />
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('file.upload.prompt')}
              {/* The button part of the text is handled by the onClick on the div now */}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('file.upload.maxFilesInfo', { maxFiles, defaultValue: `Max ${maxFiles} files. Accepted types: Images, PDF, Office Docs, Archives.` })}
            </p>
          </div>
        )
      )}

      {/* Upload Progress (for newly uploading files) */}
      {(() => {
        const activeUploads = uploadProgress.filter(p => ['validating', 'converting', 'uploading', 'error'].includes(p.stage));
        if (activeUploads.length === 0) {
          return null;
        }
        return (
          <div className="space-y-2 mt-3">
            {activeUploads.map((progressItem) => {
              // Determine displayed text and progress based on stage
              let statusText = '';
              let progressBarWidth = 0;
              let progressIcon = null;

              switch (progressItem.stage) {
                case 'pending':
                  statusText = t('file.upload.status.pending', { defaultValue: 'Pending' });
                  progressBarWidth = 0;
                  progressIcon = <Loader className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />;
                  break;
                case 'validating':
                  statusText = t('file.upload.status.validating', { defaultValue: 'Validating file...' });
                  progressBarWidth = 5; // Small initial progress for validation
                  progressIcon = <Loader className="w-4 h-4 animate-spin text-blue-500 dark:text-blue-400" />;
                  break;
                case 'converting':
                  const converted = progressItem.convertedPages || 0;
                  const total = progressItem.totalPages || 1;
                  progressBarWidth = (converted / total) * 100;
                  statusText = t('file.upload.status.converting', {
                    defaultValue: `Converting PDF page ${converted} of ${total}...`,
                    converted: converted,
                    total: total
                  });
                  progressIcon = <Loader className="w-4 h-4 animate-spin text-purple-500 dark:text-purple-400" />;
                  break;
                case 'uploading':
                  progressBarWidth = progressItem.progress; // Use the upload progress
                  statusText = t('file.upload.status.uploading', {
                    defaultValue: `Uploading ${progressItem.file.name}... ${progressItem.progress}%`,
                    fileName: progressItem.file.name,
                    progress: progressItem.progress
                  });
                  progressIcon = <Loader className="w-4 h-4 animate-spin text-blue-500 dark:text-blue-400" />;
                  break;
                case 'completed':
                  statusText = t('file.upload.status.completed', { defaultValue: 'Upload complete!' });
                  progressBarWidth = 100;
                  progressIcon = <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />;
                  break;
                case 'error':
                  statusText = t('file.upload.status.error', {
                    defaultValue: `Error: ${progressItem.error || 'Unknown error'}`,
                    error: progressItem.error || 'Unknown error'
                  });
                  progressBarWidth = 0; // Or keep last known progress?
                  progressIcon = <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400" />;
                  break;
              }

              return (
                <div key={progressItem.id} className={`rounded p-3 ${progressItem.stage === 'error' ? 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700' : 'bg-gray-100 dark:bg-slate-700'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex-1 mr-2" title={progressItem.file.name}>
                      {progressItem.file.name}
                    </span>
                    <div className="flex items-center space-x-2">
                      {progressIcon}
                    </div>
                  </div>
                  {/* Progress bar should show for validating, converting, and uploading */}
                  {['validating', 'converting', 'uploading'].includes(progressItem.stage) && ( // Show bar for these stages
                    <div className="w-full bg-gray-300 dark:bg-slate-600 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-150"
                        style={{
                          width: `${progressBarWidth}%`,
                          backgroundColor: progressItem.stage === 'converting' ? '#a855f7' : '#3b82f6' // Purple for converting, blue for uploading
                        }}
                      />
                    </div>
                  )}
                  {/* Display detailed status text or error message */}
                  <p className={`text-xs mt-1 ${progressItem.stage === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>
                    {statusText}
                    {progressItem.stage !== 'error' && progressItem.status === 'completed' && ` (${t('file.upload.status.overallCompleted', { defaultValue: 'Overall completed' })})`} // Add overall completed text if needed
                  </p>

                </div>
              );
            })}
          </div>
        );
      })()}

      {/* The list of existing attachments (attachments.map(...)) and file summary are intentionally REMOVED from this component */}
    </div>
  );
};
